import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import { EmptyView, SlimEmptyView } from "../../../../../../Components/Empty";
import { Icon } from "../../../../../../Components/Icon";
import { PaginatedGrid } from "../../../../../../Components/PaginatedGrid";
import { Tile } from "../../../../../../Components/Tile";
import {
	PersonFilterInput,
	Student,
	StudentDetailQuery,
	StudentNameFragment,
	useAddressListQuery,
	useStudentNamesDescriptionsAndImagesQuery,
} from "../../../../../../Utilities";
import { RelationshipFormat } from "../../../../../Format";
import { StudentLink } from "../../../../../Links";
import * as AddressComponents from "../../../../Address";
import * as GuardianComponents from "../../../../Guardian";
import * as RelationshipComponents from "../../../../Relationship";
import * as StudentComponents from "../../../../Student";
import { DetailRouteContext } from "../../RouteContext";

export type FamilyAndContactBodyProps = {
	student: StudentDetailQuery["student"];
};

export function FamilyAndContactBody({ student }: FamilyAndContactBodyProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);
	useActivePageRegistration(subPageInfos.familyAndContact);

	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column justify="spaced-start" style={{ width: "25rem", flexShrink: 0 }}>
				<AddressesSection student={student} style={{ flexGrow: 1 }} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<GuardiansSection student={student} />
				<RelatedStudentsSection student={student} />
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	student: StudentDetailQuery["student"];
} & Partial<ClassNameProps & StyleProps>;

function AddressesSection({ student, className, style }: PageSectionProps) {
	const { loading, data } = useAddressListQuery({
		variables: {
			filters: {
				people: [
					{
						or: [
							{
								student: [{ idEQ: student.id }],
							},
							{
								guardian: [{ relationships: [{ studentIDEQ: student.id }] }],
							},
						],
					},
				],
			},
		},
	});
	const addresses = React.useMemo(() => data?.addressConnection.edges.map((e) => e.node), [data]);

	const initialCreatePeopleFilter = React.useMemo<PersonFilterInput>(
		() => ({
			guardian: [{ relationships: [{ studentIDEQ: student.id }] }],
		}),
		[student.id],
	);

	return (
		<AddressComponents.AddressListSection
			isLoading={loading}
			addresses={addresses}
			Container={Tile}
			emptyView="full"
			initialCreatePeopleFilter={initialCreatePeopleFilter}
			createFormNameSuffix={student.id}
			className={className}
			style={style}
		/>
	);
}

function GuardiansSection({ student, className, style }: PageSectionProps) {
	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="space-between" align="center">
					<Heading.H2 noMargin>Guardians</Heading.H2>

					<RelationshipComponents.RelationshipCreatePermissionRequired>
						<RelationshipComponents.RelationshipOnStudentCreateModalController
							studentID={student.id}
							variant="link"
							size="small"
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								<Icon.Plus size={16} /> Add Guardian
							</Row>
						</RelationshipComponents.RelationshipOnStudentCreateModalController>
					</RelationshipComponents.RelationshipCreatePermissionRequired>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Conditional>
					<If condition={student.relationships.length === 0}>
						<EmptyView
							IconComponent={Icon.AddressBook}
							primaryText="Guardians"
							secondaryText="No guardians listed"
							style={{ height: "100%" }}
						/>
					</If>
					<Else>
						<PaginatedGrid items={student.relationships} columns={2} rows={3} gap="1rem" style={{ height: "100%" }}>
							{(relationship) => (
								<GuardianRelationshipCard key={relationship.id} relationship={relationship} student={student} />
							)}
						</PaginatedGrid>
					</Else>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}

type GuardianRelationshipCardProps = {
	relationship: StudentDetailQuery["student"]["relationships"][0];
	student: Pick<Student, "id"> & StudentNameFragment;
};

function GuardianRelationshipCard({ relationship, student }: GuardianRelationshipCardProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} className="button--tile" style={{ width: "100%", minWidth: "20rem" }}>
				<Row justify="space-between" align="flex-start">
					<GuardianComponents.GuardianIconNameDescription
						guardian={relationship.guardian}
						description={RelationshipFormat.Fields.relationshipType(relationship.relationshipType)}
						noLink
					/>

					<RelationshipComponents.RelationshipPropertyIcons relationship={relationship} />
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<GuardianComponents.GuardianRelationshipModal
					relationship={relationship}
					student={student}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function RelatedStudentsSection({ student, className, style }: PageSectionProps) {
	const { loading, data } = useStudentNamesDescriptionsAndImagesQuery({
		variables: {
			filters: {
				idNEQ: student.id,
				relationships: [
					{
						guardian: [
							{
								relationships: [
									{
										studentIDEQ: student.id,
									},
								],
							},
						],
					},
				],
			},
		},
	});

	const numRelated = data?.studentConnection.edges.length ?? 0;

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Heading.H2 noMargin>{toGrammaticalNumber("Related Student", numRelated, numRelated > 0)}</Heading.H2>
			</Tile.Header>
			<Tile.Body>
				<Conditional>
					<If condition={loading && !data}>Loading...</If>
					<If condition={!!data && data.studentConnection.edges.length === 0}>
						<SlimEmptyView>No related students</SlimEmptyView>
					</If>
					<Else>
						<Row justify="spaced-start" horizontalSpacing="1.25rem" overflow="scroll">
							{data?.studentConnection.edges.map(({ node }) => (
								<StudentLink
									key={node.id}
									instance={node}
									className="link--tile link--no-text-decoration"
									style={{ minWidth: "16rem" }}
								>
									<StudentComponents.StudentIconNameDescription student={node} size="small" noLink />
								</StudentLink>
							))}
						</Row>
					</Else>
				</Conditional>
			</Tile.Body>
		</Tile>
	);
}
